/*
  Collar of Sweden – size guide popup.
  Pairs with snippets/cos-size-guide.liquid and assets/cos-size-guide.css.

  - Opens <dialog data-cos-sg> from [data-cos-sg-open], closes on ×, backdrop click and Esc.
  - Mirrors the product form: marks the selected size, greys out sold-out sizes
    (reads the theme's own .sold-out / .unavailable classes on the size radios).
  - Clicking a size in the table clicks the theme's own size radio, so price, stock and URL
    update as usual, then closes the popup.
  - Image guides open full screen in a nested <dialog>.
  - Pushes size_guide_open / size_guide_select / size_guide_image_zoom to window.dataLayer.
  - Sends size_guide_opened (guide_surface: size_popup) / size_guide_select / size_guide_image_zoom to PostHog.
    window.posthog only exists after cookie consent (snippets/collar-posthog.liquid), so nothing is sent without it.
    size_guide_opened uses the same name as the accordion event (guide_surface: accordion) so the two can be compared.
  - Loaded once from layout/theme.liquid (quick view does not run scripts inside its HTML).
  - Phones: the bottom sheet can be dragged down to close.
*/
(() => {
  if (window.CosSizeGuide) return;
  window.CosSizeGuide = true;

  const CLOSE_DELAY = 300;
  const openers = new WeakMap();
  let escapeHandled = false;
  let pressedOnBackdrop = false;

  // Quick view always renders with the same "unique" (quickview-api-quickview), so ids can repeat on a page.
  // Look things up from the nearest ancestor that contains a match, never by id across the whole document.
  const scoped = (from, selector) => {
    for (let el = from && from.parentElement; el; el = el.parentElement) {
      const hit = el.querySelector(selector);
      if (hit) return hit;
    }
    return null;
  };

  // Set when Esc (or another close request, e.g. Android back) cancels one of our dialogs. Cleared after a moment
  // so an unrelated Escape later is never swallowed.
  const markEscape = () => {
    escapeHandled = true;
    window.setTimeout(() => { escapeHandled = false; }, 500);
  };

  const unlockIfNoneOpen = () => {
    if (!document.querySelector('dialog[data-cos-sg][open]')) {
      document.documentElement.classList.remove('cos-sg-lock');
    }
  };

  const POSTHOG_EVENTS = {
    size_guide_open: 'size_guide_opened',
    size_guide_select: 'size_guide_select',
    size_guide_image_zoom: 'size_guide_image_zoom',
  };

  const track = (event, dialog, extra) => {
    const props = {
      size_guide: dialog.dataset.cosSgGuide,
      product_type: dialog.dataset.cosSgProductType,
      ...extra,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...props });

    if (window.posthog && typeof window.posthog.capture === 'function') {
      try {
        window.posthog.capture(POSTHOG_EVENTS[event] || event, {
          guide_surface: 'size_popup',
          product_handle: dialog.dataset.cosSgProductHandle || null,
          ...props,
        });
      } catch (error) {
        /* analytics must never break the popup */
      }
    }
  };

  const sizeInputs = (dialog) => {
    const form = scoped(dialog, `form[id="${dialog.dataset.cosSgForm}"]`);
    if (!form) return [];
    const wrapper = form.querySelector(`[data-option-position="${dialog.dataset.cosSgOption}"]`);
    if (!wrapper) return [];
    return Array.from(wrapper.querySelectorAll('input[type="radio"][name^="options"]'));
  };

  const sync = (dialog) => {
    const inputs = sizeInputs(dialog);
    const checked = inputs.find((input) => input.checked);
    const current = checked ? checked.value.toLowerCase() : '';

    dialog.querySelectorAll('[data-cos-sg-row]').forEach((row) => {
      const size = row.dataset.cosSgRow;
      const input = inputs.find((el) => el.value.toLowerCase() === size);
      const holder = input && input.parentElement;
      const soldOut = !input || holder.classList.contains('sold-out') || holder.classList.contains('unavailable');
      const isCurrent = size === current;
      const button = row.querySelector('[data-cos-sg-size]');
      const label = row.querySelector('[data-cos-sg-sold-out]');

      row.classList.toggle('is-current', isCurrent);
      button.setAttribute('aria-pressed', String(isCurrent));
      button.disabled = soldOut && !isCurrent;
      if (!button.dataset.labelSelect) button.dataset.labelSelect = button.getAttribute('aria-label');
      button.setAttribute('aria-label', soldOut ? button.dataset.labelSoldOut : button.dataset.labelSelect);
      if (label) label.hidden = !soldOut;
    });

    const form = scoped(dialog, `form[id="${dialog.dataset.cosSgForm}"]`);
    const label = form && form.querySelector(`[data-cos-sg-current="${dialog.id}"]`);
    if (label) label.textContent = checked ? checked.value.toUpperCase() : '';
  };

  const syncAll = () => {
    document.querySelectorAll('dialog[data-cos-sg]').forEach(sync);
    unlockIfNoneOpen(); // e.g. a dialog removed from the page while open (theme editor, quick view)
  };

  const open = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    openers.set(dialog, opener);
    sync(dialog);
    dialog.showModal();
    // Desktop: lock the page behind. Phones: no lock on <html> (it makes Safari show its toolbar again and the sheet
    // jumps); the dialog's overscroll-behavior: contain keeps the page from scrolling instead.
    if (!SHEET.matches) document.documentElement.classList.add('cos-sg-lock');
    slideIn(dialog.querySelector('.cos-sg__panel'));
    // Move focus to × without scrolling. On phones the sheet starts below the screen (slide-up animation), and a
    // plain focus() makes the browser jump to reveal it, so the sheet first shoots up and then settles.
    const close = dialog.querySelector('[data-cos-sg-close]');
    if (close) close.focus({ preventScroll: true });
    track('size_guide_open', dialog);
  };

  const onClose = (dialog) => {
    unlockIfNoneOpen();
    const opener = openers.get(dialog);
    if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
  };

  // ---------- Bottom sheet on phones: slide-up, drag down to close ----------
  // iOS Safari: (1) a CSS keyframe animation on content inside a just-opened <dialog> does not play reliably, so the
  // slide-up is run with the Web Animations API. (2) iOS decides on the FIRST touchmove whether a gesture is a scroll;
  // after that preventDefault is ignored. So the grip/header have touch-action: none (CSS) and use pointer events, and
  // the content takes over on the first downward move when it is scrolled to the top.
  const SHEET = window.matchMedia('(max-width: 767px)');
  const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const slideIn = (panel) => {
    if (!SHEET.matches || reducedMotion() || !panel.animate) return;
    panel.animate([{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], { duration: 300, easing: EASE });
  };

  const enableDrag = (dialog) => {
    const panel = dialog.querySelector('.cos-sg__panel');
    const body = dialog.querySelector('.cos-sg__body');
    if (!panel) return;
    let startY = null;
    let lastY = 0;
    let lastT = 0;
    let offset = 0;
    let speed = 0;

    const begin = (y, t) => { startY = y; lastY = y; lastT = t; offset = 0; speed = 0; panel.style.transition = 'none'; };
    const move = (y, t) => {
      offset = Math.max(0, y - startY);
      speed = (y - lastY) / Math.max(1, t - lastT);
      lastY = y;
      lastT = t;
      panel.style.transform = `translateY(${offset}px)`;
    };
    const finish = () => {
      if (startY === null) return;
      startY = null;
      const close = offset > panel.offsetHeight * 0.25 || speed > 0.5;
      panel.style.transition = `transform 0.22s ${EASE}`;
      panel.style.transform = close ? 'translateY(100%)' : '';
      window.setTimeout(() => {
        if (close && dialog.open) dialog.close();
        panel.style.transition = '';
        panel.style.transform = '';
      }, 230);
    };

    // Grip and header: pointer events (touch-action: none in CSS, so the browser never starts a scroll here).
    dialog.querySelectorAll('.cos-sg__grip, .cos-sg__head').forEach((handle) => {
      handle.addEventListener('pointerdown', (event) => {
        if (!SHEET.matches || event.button > 0 || event.target.closest('button, a')) return;
        begin(event.clientY, event.timeStamp);
        try { handle.setPointerCapture(event.pointerId); } catch (error) { /* ignore */ }
      });
      handle.addEventListener('pointermove', (event) => {
        if (startY !== null) move(event.clientY, event.timeStamp);
      });
      handle.addEventListener('pointerup', finish);
      handle.addEventListener('pointercancel', finish);
    });

    // Content: drag down only when it is scrolled to the top, decided on the very first move.
    let touchY = null;
    let decided = false;
    let dragging = false;
    if (!body) return;
    body.addEventListener('touchstart', (event) => {
      if (!SHEET.matches || event.touches.length !== 1) return;
      touchY = event.touches[0].clientY;
      decided = false;
      dragging = false;
    }, { passive: true });
    body.addEventListener('touchmove', (event) => {
      if (touchY === null) return;
      const y = event.touches[0].clientY;
      if (!decided) {
        decided = true;
        dragging = y > touchY && body.scrollTop <= 0 && event.cancelable;
        if (dragging) begin(touchY, event.timeStamp);
      }
      if (!dragging) return;
      event.preventDefault();
      move(y, event.timeStamp);
    }, { passive: false });
    const touchEnd = () => {
      if (dragging) finish();
      touchY = null;
      dragging = false;
    };
    body.addEventListener('touchend', touchEnd);
    body.addEventListener('touchcancel', touchEnd);
  };

  const bind = (dialog) => {
    if (dialog.dataset.cosSgBound) return;
    dialog.dataset.cosSgBound = 'true';

    dialog.addEventListener('close', () => onClose(dialog));
    enableDrag(dialog);
    // Esc closes the dialog natively on keydown. Remember it, so the keyup does not also close quick view.
    dialog.addEventListener('cancel', markEscape);

    // Backdrop: the dialog element itself is the full-screen transparent layer around the panel.
    // Only close when the press also started there (not when a text selection inside the panel ends outside).
    dialog.addEventListener('pointerdown', (event) => { pressedOnBackdrop = event.target === dialog; });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        if (pressedOnBackdrop) dialog.close();
        pressedOnBackdrop = false;
        return;
      }

      if (event.target.closest('[data-cos-sg-close]')) {
        dialog.close();
        return;
      }

      const sizeButton = event.target.closest('[data-cos-sg-size]');
      if (sizeButton && !sizeButton.disabled) {
        const size = sizeButton.dataset.cosSgSize;
        const input = sizeInputs(dialog).find((el) => el.value.toLowerCase() === size);
        if (!input) return;
        if (!input.checked) input.click(); // the theme's own change handler updates variant, price, stock, URL
        sync(dialog);
        track('size_guide_select', dialog, { size: size.toUpperCase() });
        window.setTimeout(() => dialog.open && dialog.close(), CLOSE_DELAY);
        return;
      }

      const zoomButton = event.target.closest('[data-cos-sg-zoom]');
      if (zoomButton) {
        const zoom = dialog.querySelector('[data-cos-sg-zoom-dialog]');
        if (!zoom) return;
        zoom.showModal();
        zoom.querySelector('[data-cos-sg-zoom-close]').focus({ preventScroll: true });
        track('size_guide_image_zoom', dialog);
      }
    });

    const zoom = dialog.querySelector('[data-cos-sg-zoom-dialog]');
    if (zoom) {
      // Tap anywhere (image, backdrop or ×) closes the enlarged image. Esc closes it natively.
      zoom.addEventListener('click', (event) => {
        event.stopPropagation();
        zoom.close();
      });
      zoom.addEventListener('cancel', markEscape);
      zoom.addEventListener('close', () => {
        const button = dialog.querySelector('[data-cos-sg-zoom]');
        if (button && dialog.open) button.focus({ preventScroll: true });
      });
    }
  };

  const init = () => {
    document.querySelectorAll('dialog[data-cos-sg]').forEach(bind);
    syncAll();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cos-sg-open]');
    if (!opener) return;
    event.preventDefault();
    const dialog = scoped(opener, `dialog[id="${opener.dataset.cosSgOpen}"]`);
    if (!dialog) return;
    bind(dialog);
    open(dialog, opener);
  });

  // The theme's quick view closes on any Escape keyup on document (theme.dev.js closeOnEscape). When Esc closed one
  // of our dialogs, stop that keyup before it reaches the theme, so quick view stays open behind the size guide.
  window.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape' || !escapeHandled) return;
    escapeHandled = false;
    event.stopImmediatePropagation();
  }, true);

  // Fired by the theme (assets/theme.dev.js, fireHookEvent) on every variant change.
  // The theme updates its .sold-out classes right after firing it, so read them on the next tick.
  document.addEventListener('theme:variant:change', () => window.setTimeout(syncAll, 0));
  // Theme editor re-renders sections.
  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
